import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import firebaseApp from "./fireconnect";
import Header from "./Header";
import Footer from "./Footer";
import HeadingNavbar from "./HeadingNavbar";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import Blog from "./Blog";
import { useDataContext } from "./DataContext";

export default function BlogDetails() {
  const { id } = useParams();
  const { blogDetails, setBlogDetails } = useDataContext();

  const [blog, setBlog] = useState(blogDetails[id] || null);
  const [loading, setLoading] = useState(!blogDetails[id]);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loadingRelatedBlogs, setLoadingRelatedBlogs] = useState(true);

  // ================= FETCH BLOG DETAILS =================
  useEffect(() => {
    const fetchBlogDetails = async () => {
      // If data is already cached, use it
      if (blogDetails[id]) {
        setBlog(blogDetails[id]);
        setLoading(false);
        return;
      }

      try {
        const db = firebaseApp.firestore();
        const doc = await db.collection("Blog").doc(id).get();

        if (doc.exists) {
          const blogData = {
            id: doc.id,
            ...doc.data(),
            formattedDate:
              doc.data().formattedDate || new Date().toLocaleDateString(),
          };
          setBlog(blogData);

          // Update cache
          setBlogDetails((prev) => ({ ...prev, [id]: blogData }));
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetails();
  }, [id, blogDetails, setBlogDetails]);

  // ================= FETCH RELATED BLOGS =================
  useEffect(() => {
    const fetchRelatedBlogs = async () => {
      try {
        setLoadingRelatedBlogs(true);
        const db = firebaseApp.firestore();
        const querySnapshot = await db.collection("Blog").get();

        const relatedBlogsData = querySnapshot.docs
          .filter((doc) => doc.id !== id)
          .slice(0, 3)
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            formattedDate:
              doc.data().formattedDate || new Date().toLocaleDateString(),
          }));

        setRelatedBlogs(relatedBlogsData);
      } catch (error) {
        console.error("Error fetching related blogs:", error);
      } finally {
        setLoadingRelatedBlogs(false);
      }
    };

    fetchRelatedBlogs();
  }, [id]);

  // ================= SHARE =================
  const handleShare = () => {
    if (navigator.share && blog) {
      navigator.share({
        title: blog.title,
        text: blog.title,
        url: window.location.href,
      });
    }
  };

  return (
    <>
      <HeadingNavbar />
      <Header />

      <Container>
        {/* ================= LOADING SPINNER ================= */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : blog ? (
          /* ================= REAL BLOG UI ================= */
          <div className="row justify-content-center my-lg-5 my-4">
            <div className="col-lg-10 col-12">

              <div className="d-flex justify-content-end">
                <Button variant="link" onClick={handleShare} className="p-0">
                  <i className="bi bi-box-arrow-up fs-4 ShareIcon mb-3"></i>
                </Button>
              </div>

              <img
                src={blog.image}
                alt={blog.title}
                className="img-fluid mb-4 rounded-4"
                loading="lazy"
              />

              <p className="BlogformattedDate mb-3">
                {blog.formattedDate}
              </p>

              <p className="fs-3 Blogtitle fw-bold mb-4">
                {blog.title}
              </p>

              <div dangerouslySetInnerHTML={{ __html: blog.content }} />
            </div>
          </div>
        ) : (
          <div className="text-center my-5">
            <p>Blog not found.</p>
          </div>
        )}

        {/* ================= RELATED BLOGS ================= */}
        {!loadingRelatedBlogs && relatedBlogs.length > 0 && (
          <Row className="mt-lg-5 mt-0">
            <section>
              <div className="text-center section-title my-lg-5 my-4 mt-5">
                <h3 className="fw-bold text-dark">Related Blogs</h3>
              </div>
            </section>
            <Col md={12} className="p-0">
              <Blog excludeId={id} />
            </Col>
          </Row>
        )}
      </Container>

      <Footer />
    </>
  );
}
